<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="modelValue"
        class="modal-mask"
        @click="handleBackdropClick"
      >
        <div class="modal d-block" tabindex="-1">
          <div
            ref="modalDialog"
            :class="['modal-dialog', dialogClass]"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="labelledBy"
            :aria-label="labelledBy ? undefined : ariaLabel || undefined"
            tabindex="-1"
            @keydown.tab="trapFocus"
          >
            <div
              ref="modalContent"
              class="modal-content"
              @click.stop
              @touchstart="onTouchStart"
              @touchmove="onTouchMove"
              @touchend="onTouchEnd"
              @touchcancel="onTouchCancel"
              :style="sheetStyle"
            >
              <div v-if="backdrop !== 'static'" class="drag-handle-container">
                <div class="drag-handle"></div>
              </div>
              <div v-if="!hideHeader" class="modal-header">
                <h5 v-if="title" :id="titleId" class="modal-title">
                  {{ title }}
                </h5>
                <slot name="header"></slot>
                <button
                  v-if="!hideCloseButton"
                  type="button"
                  class="btn-close"
                  :aria-label="$t('common.close')"
                  @click="closeModal"
                ></button>
              </div>
              <div ref="modalBody" class="modal-body">
                <slot></slot>
              </div>
              <div v-if="!hideFooter" class="modal-footer">
                <slot name="footer"></slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: 'BaseModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    /* Accessible name for dialogs that render no title header (hideHeader, or
       a host that supplies its own chrome through the header slot). A dialog
       with no name is announced as just "dialog"; every headerless consumer
       passes an existing translated string here. Ignored when a title header
       is rendered, which names the dialog by reference instead. */
    ariaLabel: {
      type: String,
      default: ''
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    hideFooter: {
      type: Boolean,
      default: false
    },
    hideCloseButton: {
      type: Boolean,
      default: false
    },
    backdrop: {
      type: [Boolean, String],
      default: true
    },
    dialogClass: {
      type: String,
      default: 'modal-dialog-centered'
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      dragStartY: 0,
      dragDelta: 0,
      dragging: false,
      // Latched for the life of one gesture: see onTouchMove.
      dragScrolled: false,
      // Generated per instance, not per module: two modals open at once (the
      // onboarding wizard over the geo-mismatch dialog, say) would otherwise
      // both point aria-labelledby at the same id.
      titleId: `modal-title-${Math.random().toString(36).slice(2, 8)}`,
    };
  },
  computed: {
    sheetStyle() {
      if (this.dragging && this.dragDelta > 0) {
        return { transform: `translateY(${this.dragDelta}px)`, transition: 'none' };
      }
      return {};
    },
    /* The <h5> only renders when there is a title AND the header is shown, so
       both conditions gate the reference. An aria-labelledby pointing at an id
       that is not in the DOM resolves to an empty name, which is worse than no
       name at all: assistive tech announces the dialog as unlabelled either
       way, but the broken reference also hides the bug from audit tools. */
    labelledBy() {
      return this.title && !this.hideHeader ? this.titleId : undefined;
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleEscKey);
    window.addEventListener('app-back-button', this.handleAppBackButton);
    // Hosts that mount the modal already open (v-if around the component)
    // never fire the watcher, so the initial focus move has to happen here too.
    if (this.modelValue) {
      this.captureAndFocus();
    }
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.captureAndFocus();
      } else {
        this.restoreFocus();
      }
    },
  },
  created() {
    // Deliberately not in data(): a DOM node in reactive state would be deeply
    // proxied for no benefit, and nothing renders from it.
    this.previouslyFocused = null;
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscKey);
    window.removeEventListener('app-back-button', this.handleAppBackButton);
    // Unmounted while still open (a host that v-ifs the modal away): the
    // watcher never fires, so the opener would keep losing its focus.
    if (this.modelValue) {
      this.restoreFocus();
    }
  },
  methods: {
    /* Focus handling ported from BottomSheet.vue so both dialog primitives
       behave the same way: park focus on the container when it opens, keep Tab
       inside it, hand focus back to the opener when it closes. */
    captureAndFocus() {
      this.previouslyFocused = document.activeElement;
      this.$nextTick(() => {
        const dialog = this.$refs.modalDialog;
        if (!dialog) return;
        /* Hosts that place focus themselves (GeoMismatchModal points it at the
           recommended action) run their own watcher first, so parking focus on
           the container unconditionally would immediately undo their choice.
           Only claim focus when nothing inside the dialog has it. */
        if (dialog.contains(document.activeElement)) return;
        dialog.focus();
      });
    },
    restoreFocus() {
      /* isConnected, not just a focus() check: the opener is frequently gone by
         the time the dialog closes (a list row the dialog deleted, a button
         swapped out by the same state change). Calling focus() on a detached
         node silently does nothing, so this only skips work — but it keeps the
         intent honest rather than pretending focus was handed back. */
      if (
        this.previouslyFocused &&
        this.previouslyFocused.isConnected &&
        typeof this.previouslyFocused.focus === 'function'
      ) {
        this.previouslyFocused.focus();
      }
      this.previouslyFocused = null;
    },
    focusableItems() {
      const dialog = this.$refs.modalDialog;
      return Array.from(
        dialog?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((el) => !el.disabled && el.offsetParent !== null);
    },
    trapFocus(event) {
      const items = this.focusableItems();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      // The dialog container itself holds focus right after opening; wrap into
      // the items instead of letting Tab escape to the page behind the scrim.
      if (!items.includes(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    closeModal() {
      this.$emit('update:modelValue', false);
    },
    handleAppBackButton(event) {
      // Android hardware back (dispatched by App.vue). Close if open and
      // dismissible; consuming the event stops App.vue from also navigating.
      if (this.modelValue && this.backdrop !== 'static') {
        event.preventDefault();
        this.closeModal();
      }
    },
    handleBackdropClick(event) {
      if (this.backdrop === 'static' || this.backdrop === false) {
        return;
      }
      if (event.target.classList.contains('modal-mask')) {
        this.closeModal();
      }
    },
    handleEscKey(event) {
      if (event.key === 'Escape' && this.modelValue && this.backdrop !== 'static') {
        this.closeModal();
      }
    },
    /* Reset the modal's scroll position — for hosts that swap content in place
       (e.g. the onboarding wizard's step change), where the old step's scroll
       offset would otherwise carry over and open the new step mid-scroll.
       Both elements are reset because the scroll container differs by viewport:
       .modal-body on desktop (modal-dialog-scrollable), .modal-content on mobile
       (max-width: 768px). Hosts cannot do this themselves — the modal teleports
       to body, so closest('.modal-body') from their root resolves to null.
       Always an instant jump: a step change is a page change, and animating it
       would visibly rewind past the outgoing step's content. */
    scrollToTop() {
      const { modalBody, modalContent } = this.$refs;
      if (modalBody) modalBody.scrollTop = 0;
      if (modalContent) modalContent.scrollTop = 0;
    },
    /* The dismiss drag and the sheet's scrolling are the same gesture surface:
       these handlers sit on `.modal-content`, but the element that actually
       scrolls is its child `.modal-body`, so every scroll touch bubbles in
       here. A pull-down may therefore only mean "dismiss" when the content is
       already at its top; otherwise scrolling back up after reaching the
       bottom reads as a dismiss and closes the sheet mid-read.

       Bounded by `.modal-content` so an ancestor scroll offset (the page
       behind the sheet) can never be mistaken for the sheet's own.

       Deliberately walks only the touched element's OWN ancestors, so the
       parts of the sheet that are not inside the scroller -- the drag handle
       above `.modal-body`, and the header -- keep dismissing on a pull even
       while the body is scrolled. That is what a grabber is for, and it
       matches the platform sheets this one imitates.

       `e.target` from a real touch is always an Element (hit testing resolves
       to the containing element, never the text node inside it) -- verified
       on the Android WebView by dispatching a touch onto a text run and
       reading back `nodeType: 1`. The Element check is a type guard, not a
       branch that fires in practice. */
    contentIsScrolled(target) {
      const root = this.$refs.modalContent;
      if (!root) return false;
      let el = target instanceof Element && root.contains(target) ? target : root;
      while (el) {
        if (el.scrollTop > 0) return true;
        if (el === root) return false;
        el = el.parentElement;
      }
      return false;
    },
    onTouchStart(e) {
      if (window.innerWidth > 768) return;
      if (this.backdrop === 'static') return;
      if (this.contentIsScrolled(e.target)) return;
      this.dragStartY = e.touches[0].clientY;
      this.dragging = true;
      this.dragScrolled = false;
    },
    onTouchMove(e) {
      if (!this.dragging) return;
      /* Re-checked on every move, not just on press: one unbroken gesture can
         scroll away from the top and come back.

         Latched rather than re-tested, because `dy` is measured from the
         original press: a long swipe back up scrolls the content to its top
         partway through, and the travel remaining after it settles there is
         on its own enough to clear the threshold. Once a gesture has scrolled
         the content at all it stays a scroll until the finger lifts. */
      if (this.dragScrolled || this.contentIsScrolled(e.target)) {
        this.dragScrolled = true;
        this.dragDelta = 0;
        return;
      }
      const dy = e.touches[0].clientY - this.dragStartY;
      this.dragDelta = Math.max(0, dy);
    },
    onTouchEnd() {
      if (!this.dragging) return;
      this.dragging = false;
      const el = this.$refs.modalContent;
      const threshold = el ? el.offsetHeight * 0.4 : 150;
      if (this.dragDelta > threshold && this.backdrop !== 'static') {
        this.closeModal();
      }
      this.dragDelta = 0;
      this.dragScrolled = false;
    },
    /* The OS takes the gesture away (incoming call, system alert, an edge-swipe
       claimed as a back navigation) by firing touchcancel INSTEAD of touchend.
       Without this the sheet keeps `dragging` true and `dragDelta` frozen, so
       it sits translated part-way down the screen until some unrelated later
       touch resets it. A cancelled gesture is not a decision either, so this
       releases the sheet back to its resting position and never dismisses. */
    onTouchCancel() {
      this.dragging = false;
      this.dragDelta = 0;
      this.dragScrolled = false;
    },
  }
};
</script>

<style scoped>
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--app-height, 100%);
  /* The native WebView renders under the status bar / notch, so a dialog sized
     against the raw viewport collides with the clock + camera cutout. Reserving
     the inset as padding bounds the sheet below it; shrinking max-height alone
     just re-centers the dialog and clips again. --safe-top is injected natively
     (MainActivity.java / MainViewController.swift) because Android's WebView
     reports env(safe-area-inset-top) as 0; the env() fallback covers web, where
     both resolve to 0 and this is inert. */
  padding-top: var(--safe-top, env(safe-area-inset-top, 0px));
  /* Deliberately no height transition: platforms that resize for the keyboard do so
     continuously across the animation, and a CSS transition restarts on every
     resize, leaving the sheet chasing the keyboard instead of tracking it. */
  /* DESIGN.md reserves heavy black shadows for modal scrims only; this is that exception. */
  background-color: var(--al-scrim);
  /* Above the navigation layer (sidebar 2002, hamburger 2100): a modal must
     cover the whole app shell, or the nav stays clickable behind the scrim.
     Still below BillingFinalizingOverlay (3000) and toasts (9999). */
  z-index: 2150;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
}

.modal {
  position: relative;
  z-index: 2155;
  /* Auto margins (not align-items: center on the mask) center dialogs that fit
     while top-aligning + scrolling tall ones. align-items: center would clip
     the top of an overflowing dialog with no way to scroll back to it. */
  margin: auto 0;
}

/* The dialog box is a programmatic focus target only (tabindex="-1", focused
   on open so the reader lands inside the dialog); it is never reachable by Tab,
   so a ring on it marks nothing the user can act on. Interactive children keep
   their own :focus-visible rings. Same treatment as BottomSheet.vue. */
.modal-dialog:focus {
  outline: none;
}

/* Transition animations */
.modal-enter-active {
  transition: opacity var(--al-dur-short) var(--al-ease-enter);
}
.modal-leave-active {
  transition: opacity var(--al-dur-short) var(--al-ease-exit);
}

.modal-enter-active .modal-dialog {
  transition: transform var(--al-dur-short) var(--al-ease-enter);
}
.modal-leave-active .modal-dialog {
  transition: transform var(--al-dur-short) var(--al-ease-exit);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-dialog,
.modal-leave-to .modal-dialog {
  transform: scale(0.9);
}

/* Drag handle — hidden on desktop */
.drag-handle-container {
  display: none;
}

/* Bottom sheet on mobile */
@media (max-width: 768px) {
  .modal-mask {
    align-items: flex-end;
  }

  /* utilities.css sets .modal to height:100%, which pins this box to the full
     viewport so it overflows the mask's insets instead of sitting inside them.
     Shrink-to-fit lets the mask's padding actually bound the sheet. The
     descendant selector outranks the bare .modal on specificity; a source-order
     tie would not be safe, as the prod CSS minifier reorders those (#573). */
  .modal-mask .modal {
    width: 100%;
    height: auto;
    max-height: 100%;
    margin: 0;
    /* utilities.css also gives .modal 1.75rem of padding for desktop breathing
       room. On mobile that fights the bottom-sheet layout below (full-bleed
       width, top-only radius) and eats 56px of the height budget, spilling the
       sheet past the viewport. */
    padding: 0;
  }

  .modal-dialog {
    max-width: 100%;
    margin: 0;
    min-height: auto;
  }

  /* Must out-specify utilities.css's .modal-dialog-scrollable .modal-content
     (max-height: calc(100vh - 3.5rem)), which otherwise wins the tie against a
     bare .modal-content and reintroduces both bugs: 100vh spans the status bar
     and never shrinks for the keyboard. */
  .modal-dialog .modal-content {
    border-radius: var(--al-radius-lg) var(--al-radius-lg) 0 0;
    /* Budget the real occlusions instead of a flat proportion of the screen.
       --app-height already excludes the keyboard (see App.vue), so the sheet
       shrinks and scrolls rather than being pushed off the top of the screen
       with the focused input on it. */
    max-height: calc(
      var(--app-height, 100vh) - var(--safe-top, env(safe-area-inset-top, 0px)) -
        1rem
    );
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    transition: transform var(--al-dur-short) var(--al-ease-move);
  }

  .drag-handle-container {
    display: flex;
    justify-content: center;
    padding: 8px 0 4px;
    cursor: grab;
  }

  .drag-handle {
    width: 40px;
    height: 4px;
    background: var(--al-border);
    border-radius: 2px;
  }

  .modal-enter-from .modal-content,
  .modal-leave-to .modal-content {
    transform: translateY(100%);
  }

  .modal-enter-from .modal-dialog,
  .modal-leave-to .modal-dialog {
    transform: none;
  }
}

/* Motion is for orientation, not decoration. Collapse it when requested. */
@media (prefers-reduced-motion: reduce) {
  .modal-enter-active,
  .modal-leave-active,
  .modal-enter-active .modal-dialog,
  .modal-leave-active .modal-dialog,
  .modal-content,
  .modal-mask {
    transition-duration: 0.01ms;
  }
}
</style>